import React, { useEffect, useRef } from 'react';
import classes from './Confetti.module.scss';
import { Portal } from '../Portal';

export type ConfettiViewProps = {
  confettiesNumber: number;
  confettiRadius: number;
};

type XYCoordType = { x: number; y: number };

// Define the structure for a confetti piece
export type ConfettiPiece = {
  position: XYCoordType;
  initialPosition: XYCoordType;
  speed: XYCoordType;
  finalSpeedX: number;
  rotationSpeed: number;
  dragCoefficient: number;
  radius: XYCoordType;
  initialRadius: number;
  rotationAngle: number;
  radiusYDirection: 'down' | 'up';
  absCos: number;
  absSin: number;
  color: string;
  createdAt: number;
  direction: 'left' | 'right';
};

const confettiColors = ['#fcf403', '#62fc03', '#f4fc03', '#03e7fc', '#03fca5', '#a503fc', '#fc03ad', '#fc03c2'];

const setCanvasSize = (canvas: HTMLCanvasElement) => {
  canvas.width = window.innerWidth * window.devicePixelRatio;
  canvas.height = window.innerHeight * window.devicePixelRatio;
  canvas.style.width = window.innerWidth + 'px';
  canvas.style.height = window.innerHeight + 'px';
};

const getRandomItem = <T,>(array: T[]): T => {
  return array[Math.floor(Math.random() * array.length)];
};

const getScaleFactor = () => {
  return Math.log(window.innerWidth) / Math.log(1920);
};

const DEG_TO_RAD = Math.PI / 180;

// Function to create a single confetti piece
const createConfettiPiece = (
  initialPosition: XYCoordType,
  direction: 'left' | 'right',
  radius: number,
  colors: string[]
): ConfettiPiece => {
  const speedFactor = getRandomInRange(0.9, 1.7, 3) * getScaleFactor();
  const speed = { x: speedFactor, y: speedFactor };
  const finalSpeedX = getRandomInRange(0.2, 0.6, 3);
  const rotationSpeed = getRandomInRange(0.03, 0.07, 3) * getScaleFactor();
  const dragCoefficient = getRandomInRange(0.0005, 0.0009, 6);
  const rad = { x: radius, y: radius };
  const initialRadius = radius;
  const rotationAngle = direction === 'left' ? getRandomInRange(0, 0.2, 3) : getRandomInRange(-0.2, 0, 3);
  const radiusYDirection: 'down' | 'up' = 'down';

  const angle = direction === 'left' ? getRandomInRange(82, 15) * DEG_TO_RAD : getRandomInRange(-15, -82) * DEG_TO_RAD;
  const absCos = Math.abs(Math.cos(angle));
  const absSin = Math.abs(Math.sin(angle));
  const offset = getRandomInRange(-150, 0);
  const position = {
    x: initialPosition.x + (direction === 'left' ? -offset : offset) * absCos,
    y: initialPosition.y - offset * absSin
  };

  return {
    position: { ...position },
    initialPosition: { ...position },
    speed,
    finalSpeedX,
    rotationSpeed,
    dragCoefficient,
    radius: { ...rad },
    initialRadius,
    rotationAngle,
    radiusYDirection,
    absCos,
    absSin,
    color: getRandomItem(colors),
    createdAt: Date.now(),
    direction
  };
};

const getRandomInRange = (min: number, max: number, precision: number = 0) => {
  const multiplier = Math.pow(10, precision);
  const randomValue = Math.random() * (max - min) + min;
  return Math.floor(randomValue * multiplier) / multiplier;
};

export const ConfettiView = (props: ConfettiViewProps): JSX.Element => {
  const { confettiesNumber, confettiRadius } = props;
  const canvasRef = useRef<HTMLCanvasElement>(null);

  // we use useEffect here only becayse we want to make sure it runs ater we mount the canvas to the stage.
  useEffect(() => {
    const canvas = canvasRef.current;
    const context = canvas?.getContext('2d');
    let animationFrameId = 0;
    if (canvas && context) {
      // Set up canvas size
      setCanvasSize(canvas);
      // Create an array of confetti pieces
      const pieces: ConfettiPiece[] = [];

      const baseY = (5 * window.innerHeight) / 7;
      for (let i = 0; i < confettiesNumber / 2; i++) {
        pieces.push(createConfettiPiece({ x: 0, y: baseY }, 'right', confettiRadius, confettiColors));
        pieces.push(createConfettiPiece({ x: window.innerWidth, y: baseY }, 'left', confettiRadius, confettiColors));
      }

      // Animation loop using requestAnimationFrame
      let lastUpdated = Date.now();

      const updateAndDraw = () => {
        const currentTime = Date.now();
        const deltaTime = currentTime - lastUpdated;
        lastUpdated = currentTime;

        // Clear the canvas
        context.clearRect(0, 0, canvas.width, canvas.height);

        // Loop backwards to allow removal of off-screen pieces
        for (let i = pieces.length - 1; i >= 0; i--) {
          const piece = pieces[i];
          const elapsed = currentTime - piece.createdAt;

          if (piece.speed.x > piece.finalSpeedX) {
            piece.speed.x -= piece.dragCoefficient * deltaTime;
          }

          piece.position.x += piece.speed.x * (piece.direction === 'left' ? -piece.absCos : piece.absCos) * deltaTime;
          piece.position.y =
            piece.initialPosition.y - piece.speed.y * piece.absSin * elapsed + (0.00125 * Math.pow(elapsed, 2)) / 2;

          // Update vertical oscillation of the confetti shape
          piece.rotationSpeed = Math.max(piece.rotationSpeed - 1e-5 * deltaTime, 0);
          if (piece.radiusYDirection === 'down') {
            piece.radius.y -= deltaTime * piece.rotationSpeed;
            if (piece.radius.y <= 0) {
              piece.radius.y = 0;
              piece.radiusYDirection = 'up';
            }
          } else {
            piece.radius.y += deltaTime * piece.rotationSpeed;
            if (piece.radius.y >= piece.initialRadius) {
              piece.radius.y = piece.initialRadius;
              piece.radiusYDirection = 'down';
            }
          }

          // Draw the confetti piece
          const { x, y } = piece.position;
          const { x: radiusX, y: radiusY } = piece.radius;
          const scale = window.devicePixelRatio;
          context.fillStyle = piece.color;
          context.beginPath();
          context.ellipse(x * scale, y * scale, radiusX * scale, radiusY * scale, piece.rotationAngle, 0, 2 * Math.PI);
          context.fill();

          // Remove the piece if it's off the canvas
          if (piece.position.y > canvas.height / window.devicePixelRatio + 100) {
            pieces.splice(i, 1);
          }
        }

        // Continue animation if there are pieces remaining
        if (pieces.length > 0) {
          animationFrameId = requestAnimationFrame(updateAndDraw);
        }
      };

      updateAndDraw();
    }

    return () => {
      cancelAnimationFrame(animationFrameId);
    };
  }, [confettiesNumber, confettiRadius]);
  return (
    <Portal>
      <canvas ref={canvasRef} className={classes.Confetti} />
    </Portal>
  );
};
